/**
 * Lazy-loads the EGM96 geoid model and converts MSL (mean sea level) elevation
 * to HAE (height above ellipsoid) for use with caller altitude in floor estimation.
 *
 * The EGM96 .pgm file must be hosted as a static asset at the URL below.
 * Download from: https://geographiclib.sourceforge.io/html/geoid.html (egm96-15.pgm).
 */
import { Geoid, parsePGM } from '@math.gl/geoid';

/** URL or path candidates for the EGM96 15-minute .pgm file. */
const EGM96_PGM_URL_CANDIDATES = Array.from(
  new Set(
    [
      typeof process !== 'undefined' ? process.env?.EGM96_PGM_URL : undefined,
      '/egm96-15.pgm',
      '/assets/egm96-15.pgm',
      '/static/egm96-15.pgm',
    ].filter((url): url is string => Boolean(url)),
  ),
);

let geoidInstance: Geoid | null = null;
let geoidLoadPromise: Promise<Geoid | null> | null = null;

/**
 * Load the EGM96 geoid model from the configured .pgm file (lazy, cached).
 * Returns null if the file cannot be loaded.
 */
async function getGeoid(): Promise<Geoid | null> {
  if (geoidInstance) {
    return geoidInstance;
  }
  if (geoidLoadPromise) {
    return geoidLoadPromise;
  }

  geoidLoadPromise = (async () => {
    try {
      for (const url of EGM96_PGM_URL_CANDIDATES) {
        const response = await fetch(url);
        if (!response.ok) {
          continue;
        }

        const arrayBuffer = await response.arrayBuffer();
        const data = new Uint8Array(arrayBuffer);
        geoidInstance = parsePGM(data, { cubic: true });
        return geoidInstance;
      }

      geoidLoadPromise = null; // Allow retry on next call
      return null;
    } catch {
      geoidLoadPromise = null; // Allow retry on next call
      return null;
    }
  })();

  return geoidLoadPromise;
}

/**
 * Convert mean sea level (MSL) elevation to height above ellipsoid (HAE).
 * Uses the EGM96 geoid model; the geoid file is lazy-loaded on first use.
 *
 * @param lat - Latitude in degrees
 * @param lon - Longitude in degrees
 * @param mslElevation - Elevation in meters (MSL)
 * @returns Elevation in meters (HAE), or null if the geoid cannot be loaded
 */
export async function mslToHae(
  lat: number,
  lon: number,
  mslElevation: number,
): Promise<number | null> {
  const geoid = await getGeoid();
  if (!geoid) {
    return null;
  }
  const undulation = geoid.getHeight(lat, lon);
  if (Number.isNaN(undulation)) {
    return null;
  }
  return mslElevation + undulation;
}
